// =============================================================================
// Suffix-hoisting index — the SINGLE source of truth for v0.8.2 name hoisting
// (§4.11). A reference may name a module/interface by any UNIQUE path-suffix
// (`Auth`, `B.Auth`), never the bare interface leaf alone. Three consumers read
// this: the stub classifier (membership), the validator (membership + ambiguity),
// and the graph (unique-winner resolution). Keeping the walk + the suffix rule
// here means a future change (e.g. space-scoping) happens in ONE place.
// =============================================================================

import type { ResolvedModel, ResolvedModule, ResolvedSurface } from "./resolver.js";
import { spaceChain } from "./resolver.js";
import type { QualifiedName } from "./types.js";

/**
 * Visit every module (with its full path segments) and every interface (with its
 * OWNER's path segments + leaf name) in a resolved model. Surfaces are part of an
 * interface's owner path (§4.2), so an interface under `M.Api` is visited with
 * owner `[…, M, Api]`. This is the canonical traversal every hoisting index shares.
 * `moduleSegs` is the owning MODULE's path (the owner path minus any surface
 * segments) — the elision boundary (§4.11 surface elision).
 */
export function walkSymbols(
  modules: readonly ResolvedModule[],
  onModule: (segs: readonly string[], module: ResolvedModule) => void,
  onInterface: (
    ownerSegs: readonly string[],
    leaf: string,
    moduleSegs: readonly string[],
    ownerModule: ResolvedModule
  ) => void
): void {
  const visitSurface = (
    surfaces: readonly ResolvedSurface[],
    parentSegs: readonly string[],
    moduleSegs: readonly string[],
    owner: ResolvedModule
  ): void => {
    for (const f of surfaces) {
      const p = [...parentSegs, f.name];
      for (const i of f.interfaces) onInterface(p, i.name, moduleSegs, owner);
      visitSurface(f.surfaces, p, moduleSegs, owner);
    }
  };
  const visit = (m: ResolvedModule, parentSegs: readonly string[]): void => {
    const myPath = [...parentSegs, m.name];
    onModule(myPath, m);
    for (const i of m.interfaces) onInterface(myPath, i.name, myPath, m);
    visitSurface(m.surfaces, myPath, myPath, m);
    for (const child of m.modules) visit(child, myPath);
  };
  for (const m of modules) visit(m, []);
}

/**
 * Every path-suffix of a module path, leaf → full (`Common.Auth` → `Auth`,
 * `Common.Auth`). Pass `proper: true` to exclude the full path itself (the graph
 * indexes the full path directly, then adds only the shorter suffixes).
 */
export function moduleSuffixes(segs: readonly string[], proper = false): string[] {
  const out: string[] = [];
  const max = proper ? segs.length - 1 : segs.length;
  for (let k = 1; k <= max; k++) out.push(segs.slice(segs.length - k).join("."));
  return out;
}

/**
 * Every hoistable interface key: an owner path-suffix + the leaf (`Auth.login`,
 * `B.Auth.login`) — never the bare leaf, which has no stable identity. Pass
 * `proper: true` to exclude the full owner path (graph indexes that directly).
 */
export function ifaceSuffixes(ownerSegs: readonly string[], leaf: string, proper = false): string[] {
  const out: string[] = [];
  const max = proper ? ownerSegs.length - 1 : ownerSegs.length;
  for (let k = 1; k <= max; k++) {
    out.push(`${ownerSegs.slice(ownerSegs.length - k).join(".")}.${leaf}`);
  }
  return out;
}

/**
 * §4.11 surface elision: the ELIDED spellings of an interface — every module
 * path-suffix + the leaf, skipping the surface segments (`Orders.create` for
 * `Orders.OrdersResource.create`). Empty for a direct interface (no surface to
 * elide — {@link ifaceSuffixes} already enumerates its spellings) so the two
 * enumerations never double-count a key for one interface.
 */
export function elidedIfaceKeys(
  moduleSegs: readonly string[],
  ownerSegs: readonly string[],
  leaf: string
): string[] {
  if (ownerSegs.length <= moduleSegs.length) return [];
  return moduleSuffixes(moduleSegs).map((suf) => `${suf}.${leaf}`);
}

/** All hoistable spellings of one interface — owner path-suffixes plus elided
 *  (surface-skipping) module-suffix spellings, deduped. The single enumeration
 *  the unique-winner index and the ambiguity candidates share. */
export function ifaceSpellings(
  moduleSegs: readonly string[],
  ownerSegs: readonly string[],
  leaf: string
): string[] {
  const out = ifaceSuffixes(ownerSegs, leaf);
  for (const k of elidedIfaceKeys(moduleSegs, ownerSegs, leaf)) {
    if (!out.includes(k)) out.push(k);
  }
  return out;
}

/** Membership sets: every hoistable module + interface suffix in the model. */
export interface SymbolSets {
  readonly modules: Set<string>;
  readonly interfaces: Set<string>;
}

/**
 * Build the membership sets shared by the stub classifier and the validator, so
 * both agree on exactly what a hoisted reference names. (Which suffix a name
 * UNIQUELY resolves to — and whether it is ambiguous — is decided by the graph
 * and {@link buildModuleCandidates}; this set is pure membership.)
 */
export function buildSymbolSets(model: ResolvedModel): SymbolSets {
  const modules = new Set<string>();
  const interfaces = new Set<string>();
  const spaces = model.spaces;
  // v0.11 (D3): the space-EXTENDED written form (`Retail.Payments.charge` — the
  // owner's space name prefixed onto the full structural path) is how a reference
  // NAMES a twin in another space. `buildIndexes` registers it as an exact key, so
  // membership must accept it too: without it the validator raises a bogus
  // PROCESS_CALLEE_TODO and the stub classifier invents a phantom module for the
  // space prefix. Full path only — same shape the graph's `extendedKey` registers.
  const extended = (m: ResolvedModule, path: readonly string[]): string | undefined => {
    const space = spaceOfModule(m, spaces);
    return space && space.length > 0 ? [...space, ...path].join(".") : undefined;
  };
  walkSymbols(
    model.modules,
    (segs, m) => {
      for (const s of moduleSuffixes(segs)) modules.add(s);
      const ext = extended(m, segs);
      if (ext) modules.add(ext);
    },
    (ownerSegs, leaf, moduleSegs, owner) => {
      for (const s of ifaceSpellings(moduleSegs, ownerSegs, leaf)) interfaces.add(s);
      const ext = extended(owner, [...ownerSegs, leaf]);
      if (ext) interfaces.add(ext);
    }
  );
  return { modules, interfaces };
}

/**
 * §4.11 elision candidates: every hoistable interface SPELLING (owner suffixes +
 * elided module-suffix forms) → the space-tagged FULL structural paths bearing
 * it. The validator reads this for the ambiguity diagnostic (2+ distinct paths
 * behind one written form, none exact) and the did-you-mean full-path hints;
 * candidates are declared elements only (the model here is the un-stubbed one —
 * a synthesized stub is never a resolution candidate, §4.12).
 *
 * Deliberately NARROWER than {@link buildSymbolSets}: it enumerates hoistable
 * spellings only, never the D3 space-EXTENDED form. This map is also the LSP
 * formatter's elision universe (`format-elision.ts`) — what it calls unique, the
 * formatter may shorten source to. An extended spelling is a way to NAME a twin,
 * not a candidate to elide toward, so admitting it here would let the formatter
 * rewrite code on the strength of a cross-space alias. The cost is that a
 * did-you-mean hint never suggests another space's twin; that is the intended
 * trade.
 */
export function buildIfaceCandidates(model: ResolvedModel): Map<string, ScopedCand<string>[]> {
  const out = new Map<string, ScopedCand<string>[]>();
  const spaces = model.spaces;
  const visitIface = (
    ownerSegs: readonly string[],
    leaf: string,
    moduleSegs: readonly string[],
    ownerModule: ResolvedModule
  ): void => {
    const full = [...ownerSegs, leaf];
    const cand: ScopedCand<string> = {
      value: full.join("."),
      path: full,
      space: spaceOfModule(ownerModule, spaces),
    };
    for (const key of ifaceSpellings(moduleSegs, ownerSegs, leaf)) {
      const list = out.get(key);
      if (list) list.push(cand);
      else out.set(key, [cand]);
    }
  };
  const visitSurface = (
    surfaces: readonly ResolvedSurface[],
    parentSegs: readonly string[],
    moduleSegs: readonly string[],
    ownerModule: ResolvedModule
  ): void => {
    for (const f of surfaces) {
      const p = [...parentSegs, f.name];
      for (const i of f.interfaces) visitIface(p, i.name, moduleSegs, ownerModule);
      visitSurface(f.surfaces, p, moduleSegs, ownerModule);
    }
  };
  const visit = (m: ResolvedModule, parentSegs: readonly string[]): void => {
    const myPath = [...parentSegs, m.name];
    for (const i of m.interfaces) visitIface(myPath, i.name, myPath, m);
    visitSurface(m.surfaces, myPath, myPath, m);
    for (const child of m.modules) visit(child, myPath);
  };
  for (const m of model.modules) visit(m, []);
  return out;
}

/**
 * v0.8.2 hoisting index: every module path-suffix → the full paths bearing it, so
 * an ambiguous reference of ANY length (`Payments`, `Common.Auth`) can be flagged,
 * not just a bare leaf. Shared by the validator and the incremental compiler.
 */
export function buildModuleCandidates(modules: readonly ResolvedModule[]): Map<string, string[]> {
  const out = new Map<string, string[]>();
  const visit = (m: ResolvedModule, parentSegs: readonly string[]): void => {
    const segs = [...parentSegs, m.name];
    const full = segs.join(".");
    for (const suf of moduleSuffixes(segs)) {
      const list = out.get(suf);
      if (list) {
        if (!list.includes(full)) list.push(full);
      } else out.set(suf, [full]);
    }
    for (const child of m.modules) visit(child, segs);
  };
  for (const m of modules) visit(m, []);
  return out;
}

// =============================================================================
// Space-scoped resolution (§4.11) — a suffix resolves within the referrer's
// space chain (its space → enclosing spaces → package root). A suffix ambiguous
// GLOBALLY but unique among the modules VISIBLE to the referrer resolves to the
// local one; single-space models (every module at root) collapse to the global
// behavior for free. `use` brings only TYPE names today, so the candidate set is
// purely the ancestor-space chain — no per-space module imports to consult.
// =============================================================================

/** The identity space of a module. v0.11 (D1a): the resolver stamps the
 *  LANDED space onto every ResolvedModule (an `in`-placed subtree carries
 *  its target's space) — prefer it; the source-file lookup remains as the
 *  fallback for hand-built modules that predate the stamp. */
export function spaceOfModule(
  m: ResolvedModule,
  spaces: ReadonlyMap<string, QualifiedName> | undefined
): QualifiedName | undefined {
  if (m.space && m.space.length > 0) return m.space;
  const f = m.decl.source?.file;
  return f && spaces ? spaces.get(f) : undefined;
}

/** Segment-equality of two spaces (undefined = package root). */
export function sameSpace(a: QualifiedName | undefined, b: QualifiedName | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return a.length === b.length && a.every((s, i) => s === b[i]);
}

/** A module candidate for a written suffix, tagged with its declaring space. */
export interface ScopedCand<T> {
  readonly value: T;
  readonly path: readonly string[];
  readonly space: QualifiedName | undefined;
}

/**
 * Resolve a written suffix against space-tagged candidates from a referrer in
 * `referrerSpace`. Returns the sole match, `"ambiguous"`, or `undefined` (no
 * candidate). Nearest-first: the closest space on the referrer's chain that has
 * ≥1 match wins (exactly one → resolve; 2+ in that same space → ambiguous). When
 * nothing on the chain matches, a name reachable only elsewhere still resolves if
 * it is globally unique (cross-space coupling stays visible + flagged, never
 * silently dropped); genuinely-distinct globals stay ambiguous.
 */
export function resolveScoped<T>(
  cands: readonly ScopedCand<T>[] | undefined,
  referrerSpace: QualifiedName | undefined
): ScopedCand<T> | "ambiguous" | undefined {
  if (!cands || cands.length === 0) return undefined;
  if (cands.length === 1) return cands[0];
  for (const s of spaceChain(referrerSpace)) {
    const here = cands.filter((c) => sameSpace(c.space, s));
    if (here.length === 1) return here[0];
    if (here.length > 1) return "ambiguous";
  }
  // Fallback: nothing visible on the chain. A single distinct global target
  // resolves (keeps cross-space coupling statable); multiple distinct → ambiguous.
  const distinct = new Set(cands.map((c) => c.path.join(".")));
  return distinct.size === 1 ? cands[0] : "ambiguous";
}

/** suffix → the modules bearing it, each tagged with its declaring space. */
export function buildScopedModuleIndex(
  model: ResolvedModel
): Map<string, ScopedCand<ResolvedModule>[]> {
  const spaces = model.spaces;
  const out = new Map<string, ScopedCand<ResolvedModule>[]>();
  const visit = (m: ResolvedModule, parentSegs: readonly string[]): void => {
    const segs = [...parentSegs, m.name];
    const cand: ScopedCand<ResolvedModule> = { value: m, path: segs, space: spaceOfModule(m, spaces) };
    for (const suf of moduleSuffixes(segs)) {
      const list = out.get(suf);
      if (list) list.push(cand);
      else out.set(suf, [cand]);
    }
    for (const child of m.modules) visit(child, segs);
  };
  for (const m of model.modules) visit(m, []);
  return out;
}

/**
 * Resolve a WRITTEN module reference against a flat module index: exact
 * qualified match, else the unique last-segment candidate, else the unique
 * proper-suffix candidate. Ambiguity resolves to nothing — the referring pass
 * owns the diagnostic.
 *
 * This is hoisting-LITE: no space scoping, no visibility. It is the rule the
 * presence pass (which plane an inclusion lands on) and the stub pass (whether
 * that inclusion needs a ghost) must apply IDENTICALLY — a disagreement would
 * stub a target presence considered resolved, or leave a ghost planeless. One
 * definition so they cannot drift apart.
 */
export function resolveFlatModuleRef(
  segs: readonly string[],
  /** Any keyed index of known module paths — a Set of them, or a Map keyed by
   *  them (the presence pass already holds one, keyed to the module). */
  known: { has(key: string): boolean },
  byLastSeg: ReadonlyMap<string, readonly string[]>,
): string | undefined {
  const q = segs.join(".");
  if (known.has(q)) return q;
  const cands = byLastSeg.get(segs[segs.length - 1] ?? "");
  if (!cands || cands.length === 0) return undefined;
  if (cands.length === 1) return cands[0];
  const suffix = cands.filter((c) => c === q || c.endsWith(`.${q}`));
  return suffix.length === 1 ? suffix[0] : undefined;
}
